/* eslint-disable no-param-reassign */

/**
 * Convert a timestamp to a human-readable date.
 *
 * @param {String} timestamp    the timestamp as a string
 * @param {Boolean} [local]     whether to convert to local time or not
 * @param {Boolean} [dateOnly]  whether to only return the date
 *
 * @returns {String}
 */
const getReadableDate = (timestamp, local = false, dateOnly = false) => {
  const date = local
    ? new Date(timestamp.slice(0, -1))
    : new Date(timestamp)

  const day = date.getDate()
  const month = date.getMonth() + 1
  const year = date.getFullYear()
  const hours = date.getHours()
  const min = date.getMinutes()
  const time = !dateOnly ? ` ${hours}:${`0${min}`.slice(-2)}` : ''

  return `${day}.${month}.${year}${time}`
}
/**
 * Get a duration (end date - start date)
 * in human-readable form.
 *
 * @param {string|Date} t1  Start date
 * @param {string|Date} t2  End date
 * @returns {string|false}  Delta of End - Start in human-readable form
 */
const getRelativeTime = (t1, t2 = new Date()) => {
  t1 = t1 instanceof Date ? t1 : new Date(t1)
  t2 = t2 instanceof Date ? t2 : new Date(t2)

  const secondsPast = (t2.getTime() - t1.getTime()) / 1000
  const parsedSeconds = u => parseInt(secondsPast / u, 10)

  return ((secondsPast < 60) && { seconds: secondsPast })
      || ((secondsPast < 3600) && { minutes: parsedSeconds(60) })
      || ((secondsPast <= 86400) && { hours: parsedSeconds(3600) })
      || ((secondsPast <= 2628000) && { days: parsedSeconds(86400) })
      || ((secondsPast <= 31536000) && { months: parsedSeconds(2628000) })
      || ((secondsPast > 31536000) && { years: parsedSeconds(31536000) })
}

/**
 * Returns a date format, like `dd.MM.yyyy` based on a locale.
 *
 * @param {string} locale target locale, like `de`, `en`, ...
 * @returns {string}
 */
const getDateFormatFromLocale = (locale) => {
  const options = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }
  const formatter = new Intl.DateTimeFormat(locale, options)
  const parts = formatter.formatToParts(new Date())

  return parts
    .map((part) => {
      switch (part.type) {
        case 'day':
          return 'dd'
        case 'month':
          return 'MM'
        case 'year':
          return 'yyyy'
        default:
          return part.value // Keep literals like '.', '/', '-'
      }
    })
    .join('')
}

/**
 * Turns a date format, like `dd.MM.yyyy` into a mask like `__.__.____`.
 *
 * @param {string} format the format to get the mask from
 * @param {string} char   the mask character
 * @returns {string}
 */
const getDateMaskFromFormat = (format = 'dd.MM.yyyy', char = '_') => format.replace(/[a-z]/gi, char)

/**
 * Turns `date` into another date with the timezone nullified.
 *
 * @example
 * // Without this function
 * const date = new Date(Date.parse('01.01.2022'))
 * const dateString = date.toISOString(date) // 2021-12-31T23:00:00.000Z
 *
 * // With this function
 * const date = getDateWithoutTimeZone('01.01.2022')
 * const dateString = date.toISOString(date) // 2022-01-01T00:00:00.000Z
 *
 * @param {Date|string} date the date to format
 * @returns {Date}
 */
const getDateWithoutTimezone = (date) => {
  const localeDate = new Date(Date.parse(date))

  const utcDate = Date.UTC(
    localeDate.getFullYear(),
    localeDate.getMonth(),
    localeDate.getDate(),
    0,
    0,
    0,
    0,
  )

  return new Date(utcDate)
}



/**
 * Checks whether `value` is parseable as a date.
 *
 * @param {string} value date string to check
 */
const isDate = (value) => {
  const date = new Date(value)

  return !Number.isNaN(date) && date.toString() !== 'Invalid Date'
}

/**
 * Returns whether `a` is older than `b`.
 *
 * @param {Date|string} a the first date
 * @param {Date|string} b the second date
 * @returns {boolean}
 */
const isOlderThan = (a, b) => {
  const d1 = new Date(a)
  const d2 = new Date(b)

  if (Number.isNaN(d1.getTime()) || Number.isNaN(d2.getTime())) {
    throw new Error(`Invalid date inputs: A: ${a}, B: ${b}`)
  }

  return d1 < d2
}

/**
 * Returns whether `date` is older than one day.
 *
 * @param {Date|string} date the ISO-formatted date
 * @returns {boolean}
 */
const isOlderThanOneDay = (date) => {
  const now = new Date()
  const before = new Date(date)

  if (Number.isNaN(before.getTime())) {
    throw new Error(`Invalid date input: ${before}`)
  }

  const diff = Math.abs(now.getTime() - before.getTime()) / 3600000

  return diff > 24
}

/**
 * Tries to repair a potentially incomplete `date` according to `mask`.
 *
 * @param {string} mask the mask to sanitize to, like `__.__.____`
 * @param {string} date the date to sanitize, like `01.01.10`
 * @returns {string}
 */
const sanitizeDateToMask = (mask, date) => {
  // Determine the separator from the mask
  const maskSeparatorMatch = mask.match(/[^_]/)
  if (!maskSeparatorMatch) {
    throw new Error(`Invalid mask: ${mask}, expected format like '__.__.____'`)
  }

  const separator = maskSeparatorMatch[0]

  // Checking if date has separator
  if (!date.includes(separator)) {
    throw new Error(`Invalid date, does not include separator ${separator}: ${date}`)
  }

  // Splitting the mask and the date into their respective parts.
  const maskParts = mask.split(separator)
  const dateParts = date.split(separator)

  // Validate that the number of parts in the mask and date match.
  if (maskParts.length !== dateParts.length) {
    throw new Error(`Date structure mismatch: Mask has ${maskParts.length} parts, but date has ${dateParts.length} parts.`)
  }

  const completedParts = []

  // Process each part of the date.
  for (let i = 0; i < maskParts.length; i++) {
    const currentMaskPart = maskParts[i]
    let currentDatePart = dateParts[i]
    const expectedLength = currentMaskPart.length

    // Handle year completion (assuming the year is the 4-character part).
    if (expectedLength === 4 && currentDatePart.length === 2) {
      // Get the current century (e.g., 20 for the year 2025).
      const currentCentury = Math.floor(new Date().getFullYear() / 100)
      // Prepend the century to the two-digit year. "25" -> "2025".
      currentDatePart = `${currentCentury}${currentDatePart}`
    }

    // Handle padding for any part (day, month) that is too short.
    if (currentDatePart.length < expectedLength) {
      // Use padStart to add leading zeros. "1" -> "01".
      currentDatePart = currentDatePart.padStart(expectedLength, '0')
    }

    completedParts.push(currentDatePart)
  }

  return completedParts.join(separator)
}

export {
  getRelativeTime as default,
  getReadableDate,
  getDateFormatFromLocale,
  getDateMaskFromFormat,
  getDateWithoutTimezone,

  isDate,
  isOlderThan,
  isOlderThanOneDay,

  sanitizeDateToMask,
}
